/**
 * Phone-as-login helper. We don't send SMS — a phone number is canonicalised
 * to its full international form (country code + national number, digits only)
 * and mapped to a synthetic internal email so Supabase's email/password auth
 * can be reused at zero messaging cost. The real phone is stored in user
 * metadata for operator contact.
 *
 * The input MUST carry an unambiguous prefix so the same person never lands on
 * two different identities depending on how they typed it:
 *   "+20 100 049 4712" / "0020 100..." / "01000494712"  -> "201000494712"  (Egypt)
 *   "+966 50 123 4567" / "00966 50..."                    -> "966501234567"  (Saudi)
 *   "+1 415 555 1234"                                      -> "14155551234"   (US)
 * A leading single "0" with no country code is treated as Egypt — the primary
 * market, where guests type "01x…". A bare number with no "+", "00", or leading
 * "0" is ambiguous (we can't tell the country) and is rejected; the user must
 * include their country code, e.g. +20 or +966.
 */
export function canonicalizePhone(raw: string): string | null {
  const trimmed = (raw ?? "").trim();
  const digits = trimmed.replace(/\D/g, "");
  if (!digits) return null;

  let d: string;
  if (trimmed.startsWith("+")) {
    d = digits; // "+<cc>…"  → the '+' is stripped by \D, so digits already = <cc>…
  } else if (digits.startsWith("00")) {
    d = digits.slice(2); // "00<cc>…" → <cc>…
  } else if (digits.startsWith("0")) {
    d = "20" + digits.slice(1); // local "01x…" → Egypt country code (primary market)
  } else {
    // No "+", no "00", no leading "0" → country is ambiguous. Refuse rather
    // than guess (guessing would fragment one person's account across formats).
    return null;
  }

  // E.164: country code + national number, 8–15 digits total.
  if (d.length < 8 || d.length > 15) return null;
  return d;
}

/** Synthetic email used as the Supabase identity for a phone account. */
export function phoneToSyntheticEmail(canonicalPhone: string): string {
  return `${canonicalPhone}@phone.bluekeys.co`;
}

const SYNTHETIC_DOMAIN = "@phone.bluekeys.co";

/** True for the internal address a phone account is stored under. */
export function isSyntheticPhoneEmail(email: string | null | undefined): boolean {
  return (email ?? "").trim().toLowerCase().endsWith(SYNTHETIC_DOMAIN);
}

/**
 * The guest's REAL email, or "" for a phone account.
 *
 * A phone sign-up has no email: Supabase needs one, so the account is stored
 * under `<digits>@phone.bluekeys.co`. That address is plumbing — it receives
 * no mail and the guest never typed it — so it must never be prefilled into a
 * form or sent anywhere as a contact address.
 */
export function realEmailOrEmpty(email: string | null | undefined): string {
  return isSyntheticPhoneEmail(email) ? "" : (email ?? "").trim();
}

/**
 * What to SHOW as the account's identity: the real email, or for a phone
 * account the phone number itself ("+201099868988"), never the internal address.
 */
export function accountIdentityLabel(email: string | null | undefined): string {
  const e = (email ?? "").trim();
  if (!isSyntheticPhoneEmail(e)) return e;
  return `+${e.slice(0, e.toLowerCase().indexOf(SYNTHETIC_DOMAIN))}`;
}
